import type { MonsterContentId } from "./registry.generated.js";

export interface BossEncounterOffset {
  x: number;
  y: number;
}

export interface BossEncounterSummonGroup {
  monsterId: MonsterContentId;
  level: number;
  offsets: BossEncounterOffset[];
}

export interface BossEncounterSummons {
  triggerPhase: number;
  windupMs?: number;
  groups?: BossEncounterSummonGroup[];
  monsterId?: MonsterContentId;
  level?: number;
  offsets?: BossEncounterOffset[];
}

export interface BossEncounterEnrage {
  hpThreshold: number;
  damageMultiplier: number;
  movementSpeedMultiplier: number;
}

export interface BossEncounterHazard {
  mapLayer: string;
  intervalMs: number;
  telegraphMs: number;
  durationMs: number;
  damageIntervalMs: number;
  power: number;
}

interface PhasedBossEncounterFields {
  phaseHpThresholds: number[];
  wipeResetMs: number;
  enrage: BossEncounterEnrage;
  summons: BossEncounterSummons;
}

export interface PhasedArenaBossEncounterDefinition extends PhasedBossEncounterFields {
  profileId: "phased_arena";
  hazard?: never;
}

export interface PhasedHazardBossEncounterDefinition extends PhasedBossEncounterFields {
  profileId: "phased_hazard_arena";
  hazard: BossEncounterHazard;
}

export type BossEncounterDefinition =
  PhasedArenaBossEncounterDefinition | PhasedHazardBossEncounterDefinition;

export function isBossEncounterDefinition(
  value: unknown,
): value is BossEncounterDefinition {
  if (!value || typeof value !== "object") return false;
  const definition = value as Partial<PhasedBossEncounterFields> & {
    profileId?: unknown;
    hazard?: unknown;
  };
  return (
    (definition.profileId === "phased_arena" ||
      definition.profileId === "phased_hazard_arena") &&
    Array.isArray(definition.phaseHpThresholds) &&
    definition.phaseHpThresholds.length > 0 &&
    definition.phaseHpThresholds.every(
      (threshold, index, thresholds) =>
        Number.isFinite(threshold) &&
        threshold > 0 &&
        threshold < 1 &&
        (index === 0 || threshold < thresholds[index - 1]!),
    ) &&
    positiveInteger(definition.wipeResetMs) &&
    validEnrage(definition.enrage) &&
    validSummons(definition.summons) &&
    definition.summons.triggerPhase <=
      definition.phaseHpThresholds.length + 1 &&
    (definition.profileId === "phased_arena"
      ? definition.hazard === undefined
      : validHazard(definition.hazard))
  );
}

function positiveInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}

function validEnrage(value: unknown): value is BossEncounterEnrage {
  if (!value || typeof value !== "object") return false;
  const enrage = value as BossEncounterEnrage;
  return (
    Number.isFinite(enrage.hpThreshold) &&
    enrage.hpThreshold > 0 &&
    enrage.hpThreshold < 1 &&
    Number.isFinite(enrage.damageMultiplier) &&
    enrage.damageMultiplier >= 1 &&
    Number.isFinite(enrage.movementSpeedMultiplier) &&
    enrage.movementSpeedMultiplier >= 1
  );
}

function validSummons(value: unknown): value is BossEncounterSummons {
  if (!value || typeof value !== "object") return false;
  const summons = value as BossEncounterSummons;
  if (
    !Number.isInteger(summons.triggerPhase) ||
    summons.triggerPhase < 2 ||
    (summons.windupMs !== undefined && !positiveInteger(summons.windupMs))
  )
    return false;

  if (summons.groups !== undefined)
    return (
      Array.isArray(summons.groups) &&
      summons.groups.length > 0 &&
      summons.groups.every(validSummonGroup)
    );

  return (
    typeof summons.monsterId === "string" &&
    positiveInteger(summons.level) &&
    Array.isArray(summons.offsets) &&
    validOffsets(summons.offsets)
  );
}

function validSummonGroup(value: unknown): value is BossEncounterSummonGroup {
  if (!value || typeof value !== "object") return false;
  const group = value as BossEncounterSummonGroup;
  return (
    typeof group.monsterId === "string" &&
    positiveInteger(group.level) &&
    Array.isArray(group.offsets) &&
    validOffsets(group.offsets)
  );
}

function validOffsets(value: unknown): value is BossEncounterOffset[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((offset: unknown) => {
      if (!offset || typeof offset !== "object") return false;
      const point = offset as BossEncounterOffset;
      return Number.isFinite(point.x) && Number.isFinite(point.y);
    })
  );
}

function validHazard(value: unknown): value is BossEncounterHazard {
  if (!value || typeof value !== "object") return false;
  const hazard = value as BossEncounterHazard;
  return (
    typeof hazard.mapLayer === "string" &&
    hazard.mapLayer.length > 0 &&
    positiveInteger(hazard.intervalMs) &&
    positiveInteger(hazard.telegraphMs) &&
    positiveInteger(hazard.durationMs) &&
    positiveInteger(hazard.damageIntervalMs) &&
    Number.isFinite(hazard.power) &&
    hazard.power >= 0
  );
}
